import { Dispatch, useContext, useEffect, useRef, useState } from 'react'
import { uniq } from 'lodash'
import { ConferenceContext } from 'contexts/conference'
import { BookingContext } from 'contexts/booking'
import { BreakoutAddUserEvent, BreakoutRemoveUserEvent, BreakoutSwapUserEvent, BreakoutTeacherLinkEvent } from 'primus/types/events'
import { BreakoutRoom } from 'api/classroom/ClassroomState'
import { WseUserId } from 'api/types'
import { ConferenceParticipant } from 'contexts/conference/types'
import { rollbarService, RollbarExceptionNames } from 'services/rollbar'
import { useSubsession } from 'hooks/Subsession/useSubsession'
import { useFeature } from '@growthbook/growthbook-react'
import {
    startSubsessionWorkflow,
    stopSubsessionWorkflow,
    switchTeacherSubsessionWorkflow,
    swapUsersWorkflow,
    handleLateJoinersWorkflow,
    persistActiveRoom,
    clearActiveRoom,
    readActiveRoom,
    subsessionNameToRoomId
} from 'hooks/Subsession/subsessionHelpers'
import { SUBSESSION_DELAYS } from 'hooks/Subsession/subsessionConstants'
import { applyUsersRoomsToRooms, publishRoomDiffFromAssignments, getRoomIdForUser } from './breakoutHelpers'
import {
    logBreakoutEvent,
    formatRoomAssignments,
    clearBreakoutFaroDedup,
    setBreakoutFaroViewerClientUserId
} from 'services/breakoutFaroLogger'
import { SubsessionStatus, SubsessionUserStatus } from 'zoom/subsessionSdkEnums'

type BreakoutRoomsState = {
    rooms: BreakoutRoom[]
    isActive: boolean
    duration?: number
    startTime?: number
    teacherRoom: number | null
    mode: BorMode
    isTransitioning: boolean
    subsessionStatus?: SubsessionStatus
    userStatus?: SubsessionUserStatus

    start: (props: BreakoutsProps) => void
    swapUsersInSubSession: (props: BreakoutsProps) => Promise<void>
    setRoomData: (props: BreakoutsProps) => void
    stop: () => void
    setRoomActive: (props: BreakoutsCueCardProps) => void
    setDuration: Dispatch<React.SetStateAction<number>>
    linkTeacher: (roomId: RoomId | null) => Promise<void>
    getCurrentRoom: () => RoomId | null
    syncRooms: () => void
}
type BreakoutsProps = {
    usersRooms: Array<{ userId: WseUserId; roomId: number; indexId: number }>
    mode: BorMode
    duration: number
}
type BreakoutsCueCardProps = Omit<BreakoutsProps, 'mode'>
type RoomId = 0 | 1 | 2 | 3
type BorMode = 'speak' | 'cue-cards' | 'slides' | 'whiteboard'

// Delay before applying a received breakout start, so the Zoom audio pipeline can stabilize
// before room-based audio isolation is applied (mirrors what the sync button achieves manually).
// Non-subsession path only - subsessions have their own Zoom-managed timing.
const BREAKOUT_START_STABILIZE_DELAY_MS = 1000
const ACTIVATION_BUFFER_MS = 1000

const useBreakouts = (): BreakoutRoomsState => {
    const conference = useContext(ConferenceContext)
    const booking = useContext(BookingContext)
    const isSubsessionsEnabled = useFeature('goc-sub-session').value as boolean
    const isSyncRoomsEnabled = useFeature('goc-bor-sync-rooms').value as boolean
    const isJoined = conference.status === 'session-joined'
    const rtcSession = isJoined ? conference.rtcSession : undefined
    const subsessionClient = rtcSession?.getSubsessionClient?.()
    const subsessionManager = useSubsession(rtcSession?.getZoomSDKClient?.(), subsessionClient)

    const isTeacher = booking.status === 'validated' && booking.user.role === 'teacher'

    const getViewerRole = (): string =>
        booking.status === 'validated' ? booking.user.role : 'unknown'

    const getClientUserIdForWseUser = (wseUserId: WseUserId): string =>
        conference.status === 'session-joined'
            ? conference.participants.find(p => p.wseUserId === wseUserId)?.clientUserId ?? 'unknown'
            : 'unknown'

    const [rooms, setRooms] = useState<BreakoutRoom[]>([])
    const [isActive, setIsActive] = useState<boolean>(false)
    const [duration, setDuration] = useState<number>(5)
    const [teacherRoom, setTeacherRoom] = useState<number | null>(null)
    const [mode, setMode] = useState<BorMode>('speak')
    const [startTime, setStartTime] = useState<number>()
    const [isTransitioning, setIsTransitioning] = useState<boolean>(false)
    const hasAttemptedAutoRejoinRef = useRef<boolean>(false)
    const pendingStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const pendingStartAtRef = useRef<number | null>(null)
    const pendingAddTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([])

    const clearPendingAddTimeouts = (): void => {
        pendingAddTimeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId))
        pendingAddTimeoutsRef.current = []
    }

    const swapUsersInSubSession = async (props: BreakoutsProps): Promise<void> => {
        const currentAssignments = new Map<WseUserId, number | null>()
        rooms.forEach(room => {
            room.users.forEach(userId => {
                currentAssignments.set(userId, room.id)
            })
        })

        const swappedUsers: Array<{ userId: WseUserId; toRoomId: number | null }> = []

        props.usersRooms.forEach(({ userId, roomId: toRoomId }) => {
            const fromRoomId = currentAssignments.get(userId) ?? null
            if (fromRoomId !== toRoomId) {
                swappedUsers.push({ userId, toRoomId })
            }
        })

        if (!swappedUsers.length) return

        // New parallelized subsession swap workflow
        let success = true
        if (isSubsessionsEnabled && isActive) {
            success = await swapUsersWorkflow({
                swappedUsers,
                ssClient: subsessionClient,
                conference
            })
        }

        if (success) {
            const nextRooms = applyUsersRoomsToRooms(rooms, props.usersRooms)
            publishRoomDiffFromAssignments(rooms, nextRooms, props.usersRooms, conference, isSubsessionsEnabled)
            setRooms(nextRooms)
        }
    }

    const start = (props: BreakoutsProps): void => {
        if (conference.status === 'session-joined') {
            const wasActive = isActive
            if (isActive) {
                conference.primus.send('breakout:stop', {})
                setRooms(rooms => rooms.map(room => ({ ...room, users: [] })))
            }
            const finishStart = (): void => {
                setTeacherRoom(null)
                setIsActive(true)
                setMode(props.mode)
                setDuration(props.duration)
                void linkTeacher(null)
                setStartTime(Date.now())
                conference.primus.send('breakout:start', { timeSlot: props.duration })

                if (isSubsessionsEnabled) {
                    const nextRooms = applyUsersRoomsToRooms(rooms, props.usersRooms)
                    publishRoomDiffFromAssignments(rooms, nextRooms, props.usersRooms, conference, isSubsessionsEnabled)
                    logBreakoutEvent('breakout_started', {
                        source: 'local',
                        viewerRole: getViewerRole(),
                        currentRoom: getCurrentRoom() ?? -1,
                        roomAssignments: formatRoomAssignments(nextRooms)
                    })
                    setRooms(nextRooms)
                } else {
                    props.usersRooms.forEach(({ roomId, userId, indexId }) => addBreakOutRoomParticipant(roomId, userId, true, indexId))
                }

            }

            if (isSubsessionsEnabled) {
                const roomCount = Math.max(...props.usersRooms.map(u => u.roomId), -1) + 1

                void startSubsessionWorkflow({
                    roomCount,
                    usersRooms: props.usersRooms,
                    isTeacher,
                    wasActive,
                    subsession: subsessionManager,
                    ssClient: subsessionClient,
                    conference
                }).then(success => {
                    if (success) {
                        finishStart()
                    }
                }).catch(error => {
                    rollbarService.rollbar.error(RollbarExceptionNames.SubsessionStartFailed, {
                        error: error instanceof Error ? error.message : String(error)
                    })
                })
            } else {
                finishStart()
            }
        }
    }

    const setRoomData = (props: BreakoutsProps): void => {
        if (conference.status === 'session-joined') {
            if (isActive) {
                conference.primus.send('breakout:stop', {})
                setRooms(rooms => rooms.map(room => ({ ...room, users: [] })))

            }
            setMode(props.mode)
            const nextRooms = applyUsersRoomsToRooms(rooms, props.usersRooms)
            setRooms(nextRooms)
        }
    }

    const setRoomActive = ({ duration, usersRooms }: Omit<BreakoutsProps, 'mode'>): void => {
        if (conference.status === 'session-joined') {
            // ✅ When breakouts are NOT active, we need to compare against empty rooms
            // to ensure all breakout:add-user events are sent. If we use current rooms
            // (which may have assignments from setRoomData), the diff will find users
            // already in their rooms and skip sending events.
            const roomsSnapshot = isActive ? rooms : rooms.map(room => ({ ...room, users: [] }))

            const finishSetActive = (): void => {
                setIsActive(true)
                setDuration(duration)
                void linkTeacher(null)
                setStartTime(Date.now())
                conference.primus.send('breakout:start', { timeSlot: duration })

                setRooms(currentRooms => {
                    const nextRooms = applyUsersRoomsToRooms(currentRooms, usersRooms)
                    // Compare against empty rooms (roomsSnapshot) when starting fresh
                    // to ensure all breakout:add-user events are sent to students
                    publishRoomDiffFromAssignments(roomsSnapshot, nextRooms, usersRooms, conference, isSubsessionsEnabled)
                    if (mode === 'cue-cards') {
                        logBreakoutEvent('breakout_started_cue_cards', {
                            source: 'local',
                            viewerRole: getViewerRole(),
                            currentRoom: getCurrentRoom() ?? -1,
                            roomAssignments: formatRoomAssignments(nextRooms)
                        })
                    }
                    return nextRooms
                })
            }

            if (isSubsessionsEnabled) {
                const roomCount = Math.max(...usersRooms.map(u => u.roomId), -1) + 1

                void startSubsessionWorkflow({
                    roomCount,
                    usersRooms,
                    isTeacher,
                    wasActive: isActive,
                    subsession: subsessionManager,
                    ssClient: subsessionClient,
                    conference
                }).then(success => {
                    if (success) {
                        finishSetActive()
                    }
                }).catch(error => {
                    rollbarService.rollbar.error(RollbarExceptionNames.SubsessionStartFailed, {
                        error: error instanceof Error ? error.message : String(error)
                    })
                })
            } else {
                finishSetActive()
            }
        }
    }

    const stop = (): void => {
        if (conference.status === 'session-joined') {
            setStartTime(void 0)
            setIsActive(false)
            setTeacherRoom(null)

            // Close subsessions if enabled
            if (isSubsessionsEnabled && isTeacher) {
                void stopSubsessionWorkflow({
                    ssClient: subsessionClient,
                    isTeacher
                }).catch(error => {
                    rollbarService.rollbar.error(RollbarExceptionNames.SubsessionStopRetryFailed, {
                        error: error instanceof Error ? error.message : String(error)
                    })
                })
            }

            conference.primus.send('breakout:stop', {})
            logBreakoutEvent('breakout_stopped', {
                source: 'local',
                viewerRole: getViewerRole(),
                currentRoom: getCurrentRoom() ?? -1
            })
            clearBreakoutFaroDedup()
            // Audio is restored reactively by useBreakoutAudioIsolation (unmutes everyone when breakouts deactivate)
            // Reset auto-rejoin flag when breakouts stop
            hasAttemptedAutoRejoinRef.current = false
        }
    }

    const linkTeacher = async (room: RoomId | null): Promise<void> => {
        if (conference.status !== 'session-joined' || teacherRoom === room || isTransitioning) {
            return
        }

        setIsTransitioning(true)

        try {
            // Handle subsessions via helper workflow
            let success = true
            if (isSubsessionsEnabled && isTeacher) {
                success = await switchTeacherSubsessionWorkflow({
                    targetRoom: room,
                    conference,
                    ssClient: subsessionClient
                })
            }

            if (success) {
                logBreakoutEvent('breakout_teacher_link_changed', {
                    fromRoom: teacherRoom ?? -1,
                    toRoom: room ?? -1,
                    source: 'local',
                    viewerRole: getViewerRole()
                })
                setTeacherRoom(room)
                if (isTeacher) {
                    conference.primus.send('breakout:teacher-link', { room })
                }
            }
        } catch (error) {
            rollbarService.rollbar.error(RollbarExceptionNames.BreakoutLinkTeacherFailed, {
                error: error instanceof Error ? error.message : String(error)
            })
        } finally {
            // Enforcement: Wait before allowing another transition
            // This prevents rapid-fire switching that crashes the SDK audio state
            setTimeout(() => {
                setIsTransitioning(false)
            }, SUBSESSION_DELAYS.TRANSITION_COOLDOWN_MS)
        }
    }

    const getCurrentRoom = (): RoomId | null => {
        if (booking.status === 'validated') {
            return rooms.find(r => r.users.some(u => u === booking.user.id))?.id ?? null
        } else return null
    }

    // PRIVATE
    const addRoomParticipant = (
        roomId: number,
        userId: WseUserId,
        publish: boolean = false,
        userIndex: number | undefined = undefined
    ): void => {
        if (conference.status === 'session-joined') {
        // ✅ Single atomic update — no nested setRooms call
            setRooms(prevRooms => prevRooms.map(room => {
                if (room.id === roomId) {
                    return { ...room, users: uniq([...room.users, userId]) }
                }
                // Remove user from any other room in the same pass
                if (room.users.includes(userId)) {
                    return { ...room, users: room.users.filter(u => u !== userId) }
                }
                return room
            }))

            if (publish) {
                conference.primus.send('breakout:add-user', {
                    user: userId,
                    room: roomId,
                    index: userIndex,
                    noCheck: false
                })
            }
        }
    }

    const addBreakOutRoomParticipant = (roomId: number, userId: WseUserId, publish: boolean = false, userIndex: number | undefined = undefined): void => {
        if (conference.status === 'session-joined') {
            setRooms(rooms => rooms.map(room => {
                if (roomId !== room.id && room.users.includes(userId)) {
                    removeRoomParticipant(room.id, userId, true)
                }
                return roomId === room.id ? { ...room, users: uniq(room.users.concat(userId)) } : room
            }))

            if (publish) {
                conference.primus.send('breakout:add-user', {
                    user: userId,
                    room: roomId,
                    index: userIndex,
                    noCheck: false
                })
            }
        }
    }

    const removeRoomParticipant = (roomId: number, userId: WseUserId, publish: boolean = false): void => {
        if (conference.status === 'session-joined') {
            setRooms(rooms => rooms.map(room =>
                roomId === room.id ? { ...room, users: room.users.filter(u => u !== userId) } : room
            ))

            if (publish) {
                conference.primus.send('breakout:remove-user', {
                    user: userId,
                    room: roomId,
                    noCheck: false
                })
            }
        }
    }

    const getLowestUsersRoomId = (rooms: BreakoutRoom[], participants: ConferenceParticipant[]): number => {
        const roomsWithParticipants = rooms
            .map(room => ({
                id: room.id,
                participants: participants.filter(p => room.users.some(u => u === p.wseUserId))
            }))

        return roomsWithParticipants
            .filter(r => r.participants.length > 0)
            .reduce(
                (result, current) => current.participants.length < result.participants.length ? current : result,
                roomsWithParticipants[0]
            ).id

    }

    const rejoinRoom = (rooms: BreakoutRoom[]): void => {
        if (conference.status === 'session-joined' && booking.status === 'validated') {
            const { id: userId, previousBreakoutRoom } = booking.user

            const roomId = previousBreakoutRoom !== null
                ? previousBreakoutRoom
                : !isSubsessionsEnabled ? getLowestUsersRoomId(rooms, conference.participants) : null

            if (roomId !== null) {
                logBreakoutEvent('breakout_rejoin_attempted', {
                    previousBreakoutRoom: previousBreakoutRoom ?? -1,
                    chosenRoom: roomId,
                    clientUserId: getClientUserIdForWseUser(userId),
                    source: 'refresh',
                    viewerRole: getViewerRole()
                })
                removeRoomParticipant(roomId, userId, true)
                addRoomParticipant(roomId, userId, true)
            }
        }
    }

    useEffect(() => {
        if (!isActive) {
            setDuration(5)
            setRooms(rooms => rooms.map(room => ({ ...room, users: [] })))
        }
    }, [isActive])

    useEffect(() => {
        if (conference.status !== 'session-joined' || booking.status !== 'validated') {
            return
        }
        const viewer = conference.participants.find(p => p.wseUserId === booking.user.id)
        if (viewer) {
            setBreakoutFaroViewerClientUserId(viewer.clientUserId)
        }
    }, [conference.status, conference.participants, booking.status, booking.status === 'validated' ? booking.user.id : undefined])

    /**

     * Automatically assigns pure late-joining students to active subsessions.
     * Only triggers for users who:
     *   1. Are fully connected (appear in conference.participants)
     *   2. Have no existing room assignment in the application state
     * This ensures re-joining users (who still have a room) are not re-processed here.
     */
    useEffect(() => {
        if (conference.status !== 'session-joined' || !isActive || !isTeacher || subsessionManager.unassignedUserList.length === 0) {
            return
        }

        // Build a set of all user IDs already assigned in the app state
        const assignedWseUserIds = new Set<WseUserId>()
        rooms.forEach(room => room.users.forEach(userId => assignedWseUserIds.add(userId)))

        const newUnassignedUsers = subsessionManager.unassignedUserList.filter(unassignedUser => {
            // Must be fully connected (visible in Zoom participants)
            const isConnected = conference.participants.some(p => Number(p.clientUserId) === unassignedUser.userId)
            if (!isConnected) return false

            // Must not already have a room in the app state
            try {
                const parsed = JSON.parse(unassignedUser.displayName)
                return !assignedWseUserIds.has(parsed.id)
            } catch {
                return false
            }
        })

        if (newUnassignedUsers.length === 0) return

        const timer = setTimeout(() => {
            void (async () => {
                const results = await handleLateJoinersWorkflow({
                    unassignedUsers: newUnassignedUsers,
                    ssClient: subsessionClient
                })

                if (results.length > 0) {
                    results.forEach(({ userId, roomId }) => {
                        const currentRoomUsers = rooms.find(r => r.id === roomId)?.users.length || 0
                        addRoomParticipant(roomId, userId, true, currentRoomUsers)
                    })
                }
            })()
        }, SUBSESSION_DELAYS.LATE_JOINER_DEBOUNCE_MS)

        return () => clearTimeout(timer)
    }, [
        subsessionManager.unassignedUserList,
        isActive,
        isTeacher,
        subsessionClient,
        conference.participants,
        conference.status,
        rooms
    ])

    /**
     * Persists the active subsession room index to sessionStorage on entry, and clears
     * it when the user returns to the main session. The stored index is used by the
     * auto-rejoin effect below to reconnect after a tab refresh.
     */
    useEffect(() => {
        if (!isJoined || !isSubsessionsEnabled) return

        const { userStatus, currentSubsession } = subsessionManager

        if (userStatus === SubsessionUserStatus.InSubsession) {
            const roomId = subsessionNameToRoomId(currentSubsession.subsessionName)
            if (roomId !== null) {
                persistActiveRoom(roomId)
            }
        } else if (userStatus === SubsessionUserStatus.MainSession) {
            clearActiveRoom()
        }
    }, [isJoined, isSubsessionsEnabled, subsessionManager.userStatus, subsessionManager.currentSubsession])

    /**
     * Auto-rejoin a subsession after a page refresh.
     *
     * If a roomId was persisted to sessionStorage before the refresh, attempt to
     * rejoin once the main-session is connected and the subsession list is ready.
     *
     * NOTE: The Zoom Video SDK restricts `joinSubsession` to subsession host/cohost
     * (teachers). Students will receive an INSUFFICIENT_PRIVILEGES-style error here;
     * the catch clears the stored roomId so we don't loop. For students, the teacher
     * must re-assign them via the existing late-joiner / staff flows.
     */
    useEffect(() => {
        if (!isJoined || !isSubsessionsEnabled || !subsessionClient) return
        if (!isActive) return
        if (subsessionManager.subsessionStatus !== SubsessionStatus.InProgress) return
        if (subsessionManager.userStatus === SubsessionUserStatus.InSubsession) return
        if (subsessionManager.userStatus === SubsessionUserStatus.Joining) return
        // Staff/teachers should not auto-rejoin - they stay in main session for oversight
        if (isTeacher) return

        const savedRoomId = readActiveRoom()
        if (savedRoomId === null) return

        // Prevent multiple auto-rejoin attempts
        if (hasAttemptedAutoRejoinRef.current) return
        hasAttemptedAutoRejoinRef.current = true

        void (async () => {
            try {
                const subsessionList = subsessionClient.getSubsessionList?.() || []
                const target = subsessionList[savedRoomId]

                if (!target?.subsessionId) {
                    clearActiveRoom()
                    return
                }

                await subsessionClient.joinSubsession(target.subsessionId)
            } catch (error) {
                rollbarService.rollbar.error(RollbarExceptionNames.BreakoutAutoRejoinFailed, {
                    error: error instanceof Error ? error.message : String(error)
                })
                clearActiveRoom()
            }
        })()
    }, [
        isJoined,
        isSubsessionsEnabled,
        isActive,
        isTeacher,
        subsessionClient,
        subsessionManager.subsessionStatus,
        subsessionManager.userStatus
    ])

    /**
     * Subsession Cleanup Safeguard: Automatically closes any Zoom-side subsessions
     * if the application breakout state is inactive but the SDK is still 'InProgress'.
     * Only triggers for Teachers (staff) to resolve state drift.
     */
    useEffect(() => {
        if (!isJoined || isActive || !isSubsessionsEnabled || !isTeacher || !subsessionClient) {
            return
        }

        const isZoomSubsessionActive = subsessionManager.subsessionStatus === SubsessionStatus.InProgress

        if (isZoomSubsessionActive) {
            const cleanupTimer = setTimeout(() => {
                // Re-verify after a small delay to avoid clashing with normal stop transitions
                if (!isActive && subsessionManager.subsessionStatus === SubsessionStatus.InProgress) {
                    void stopSubsessionWorkflow({
                        ssClient: subsessionClient,
                        isTeacher: true
                    }).catch(error => {
                        rollbarService.rollbar.error(RollbarExceptionNames.SubsessionStopRetryFailed, {
                            error: error instanceof Error ? error.message : String(error)
                        })
                    })
                }
            }, SUBSESSION_DELAYS.CLEANUP_VERIFY_MS)

            return () => clearTimeout(cleanupTimer)
        }
    }, [
        isJoined,
        isActive,
        isSubsessionsEnabled,
        isTeacher,
        subsessionManager.subsessionStatus,
        subsessionClient
    ])

    useEffect(() => {
        if (conference.status === 'session-joined' && booking.status === 'validated') {
            const { breakouts } = conference.classroomStateOnJoin.dcAppData.wbData

            setIsActive(breakouts.isActive)
            setRooms(breakouts.rooms)
            setDuration(breakouts.timeSlot)
            setTeacherRoom(breakouts.teacherRoom)

            if (breakouts.startTime && breakouts.timeSlot) {
                setStartTime(breakouts.startTime)
            }

            // Rejoin room on students page reload
            if (breakouts.isActive && booking.user.role === 'student') {
                rejoinRoom(breakouts.rooms)
            }

            const applyBreakoutStart = (): void => {
                pendingStartTimerRef.current = null
                pendingStartAtRef.current = null
                setIsActive(true)
                logBreakoutEvent('breakout_started', {
                    source: 'primus',
                    viewerRole: getViewerRole(),
                    currentRoom: getCurrentRoom() ?? -1,
                    myRoom: getCurrentRoom() ?? -1
                })
            }

            const onStart = (): void => {
                if (isSubsessionsEnabled) {
                    applyBreakoutStart()
                    return
                }
                // Delay activation so the Zoom audio pipeline stabilizes before room-based
                // audio isolation is applied (same effect as the sync button, but automatic)
                pendingStartAtRef.current = Date.now() + BREAKOUT_START_STABILIZE_DELAY_MS
                pendingStartTimerRef.current = setTimeout(applyBreakoutStart, BREAKOUT_START_STABILIZE_DELAY_MS)
            }
            const onStop = (): void => {
                if (pendingStartTimerRef.current !== null) {
                    clearTimeout(pendingStartTimerRef.current)
                    pendingStartTimerRef.current = null
                    pendingStartAtRef.current = null
                }
                clearPendingAddTimeouts()
                setIsActive(false)
                setTeacherRoom(null)
                logBreakoutEvent('breakout_stopped', {
                    source: 'primus',
                    viewerRole: getViewerRole(),
                    currentRoom: getCurrentRoom() ?? -1
                })
                clearBreakoutFaroDedup()
            }

            const onUserAdded = (eventData: BreakoutAddUserEvent): void => {
                const applyAdd = (): void => {
                    logBreakoutEvent('room_assignment_changed', {
                        userId: eventData.user,
                        clientUserId: getClientUserIdForWseUser(eventData.user),
                        fromRoom: getRoomIdForUser(rooms, eventData.user) ?? -1,
                        toRoom: eventData.room,
                        source: 'primus',
                        viewerRole: getViewerRole()
                    })
                    if (isSubsessionsEnabled) {
                        addRoomParticipant(eventData.room, eventData.user)
                    } else {
                        addBreakOutRoomParticipant(eventData.room, eventData.user)
                    }
                }

                // Initial creation burst (a delayed start is pending): land just before the
                // activation, so rooms are populated when audio isolation switches on.
                // Mid-breakout adds (no pending start) stay immediate.
                const startAt = pendingStartAtRef.current
                if (!isSubsessionsEnabled && startAt !== null) {
                    const delayMs = Math.max(startAt - Date.now() - ACTIVATION_BUFFER_MS, 0)
                    const timeoutId = setTimeout(() => {
                        pendingAddTimeoutsRef.current = pendingAddTimeoutsRef.current.filter(id => id !== timeoutId)
                        applyAdd()
                    }, delayMs)
                    pendingAddTimeoutsRef.current.push(timeoutId)
                    return
                }
                applyAdd()
            }

            const onUserSwapped = (eventData: BreakoutSwapUserEvent): void => {
                logBreakoutEvent('room_assignment_changed', {
                    userId: eventData.user,
                    clientUserId: getClientUserIdForWseUser(eventData.user),
                    fromRoom: eventData.fromRoom,
                    toRoom: eventData.toRoom,
                    source: 'primus',
                    viewerRole: getViewerRole()
                })
                removeRoomParticipant(eventData.fromRoom, eventData.user)
                addRoomParticipant(eventData.toRoom, eventData.user)
            }
            const onUserRemoved = (eventData: BreakoutRemoveUserEvent): void => {
                logBreakoutEvent('room_assignment_changed', {
                    userId: eventData.user,
                    clientUserId: getClientUserIdForWseUser(eventData.user),
                    fromRoom: eventData.room,
                    toRoom: -1,
                    source: 'primus',
                    viewerRole: getViewerRole()
                })
                removeRoomParticipant(eventData.room, eventData.user)
            }

            const onTeacherChangedChannel = (eventData: BreakoutTeacherLinkEvent): void => {
                logBreakoutEvent('breakout_teacher_link_changed', {
                    fromRoom: teacherRoom ?? -1,
                    toRoom: eventData.room ?? -1,
                    source: 'primus',
                    viewerRole: getViewerRole()
                })
                setTeacherRoom(eventData.room)
            }

            conference.primus.on('breakout:start', onStart)
            conference.primus.on('breakout:stop', onStop)
            conference.primus.on('breakout:add-user', onUserAdded)
            conference.primus.on('breakout:swap-user', onUserSwapped)
            conference.primus.on('breakout:remove-user', onUserRemoved)
            conference.primus.on('breakout:teacher-link', onTeacherChangedChannel)

            // Cleanup: Remove event listeners to prevent stale closures
            return () => {
                if (pendingStartTimerRef.current !== null) {
                    clearTimeout(pendingStartTimerRef.current)
                    pendingStartTimerRef.current = null
                    pendingStartAtRef.current = null
                }
                clearPendingAddTimeouts()
                conference.primus.off('breakout:start', onStart)
                conference.primus.off('breakout:stop', onStop)
                conference.primus.off('breakout:add-user', onUserAdded)
                conference.primus.off('breakout:swap-user', onUserSwapped)
                conference.primus.off('breakout:remove-user', onUserRemoved)
                conference.primus.off('breakout:teacher-link', onTeacherChangedChannel)
            }
        }
    }, [conference.status])

    const syncRooms = (): void => {
        if (conference.status !== 'session-joined' || !isActive || !isTeacher || booking.status !== 'validated' || !isSyncRoomsEnabled) {
            return
        }

        logBreakoutEvent('breakout_sync_rooms', {
            source: 'local',
            viewerRole: getViewerRole(),
            currentRoom: getCurrentRoom() ?? -1,
            teacherRoom: teacherRoom ?? -1,
            roomAssignments: formatRoomAssignments(rooms)
        })

        // conference.primus.send('breakout:sync-rooms', { userId: teacherId })

        const assignments: Array<{ userId: WseUserId; roomId: number }> = []
        rooms.forEach(room => {
            room.users.forEach(userId => {
                assignments.push({ userId, roomId: room.id })
            })
        })

        const currentTeacherRoom = teacherRoom

        if (assignments.length === 0) {
            return
        }

        const syncDelayMs = 100
        const timeSlot = duration

        void (async () => {
            conference.primus.send('breakout:stop', {})
            await new Promise(resolve => setTimeout(resolve, syncDelayMs))

            conference.primus.send('breakout:start', { timeSlot })
            await new Promise(resolve => setTimeout(resolve, syncDelayMs))

            for (const { userId, roomId } of assignments) {
                conference.primus.send('breakout:add-user', {
                    user: userId,
                    room: roomId,
                    noCheck: false
                })
                await new Promise(resolve => setTimeout(resolve, syncDelayMs))
            }

            if (currentTeacherRoom !== null) {
                await new Promise(resolve => setTimeout(resolve, syncDelayMs))
                conference.primus.send('breakout:teacher-link', { room: currentTeacherRoom as RoomId })
            }
        })()
    }

    return {
        rooms,
        isActive,
        duration,
        teacherRoom,
        mode,
        startTime,
        isTransitioning,
        start,
        swapUsersInSubSession,
        stop,
        setDuration,
        linkTeacher,
        getCurrentRoom,
        setRoomData,
        setRoomActive,
        syncRooms,
        subsessionStatus: subsessionManager.subsessionStatus,
        userStatus: subsessionManager.userStatus
    }
}

export { useBreakouts }
export type { BreakoutRoomsState }
